import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence, useAnimation } from 'framer-motion';
import { useInView } from 'react-intersection-observer';

const Portfolio: React.FC = () => {
  const [currentProject, setCurrentProject] = useState(0);
  
  const controls = useAnimation();
  const [ref, inView] = useInView({
    threshold: 0.2,
    triggerOnce: true
  });

  const projects = [
    {
      id: 1,
      title: 'Academia Gustavo Borges – Revitalização de Ambientes Internos',
      category: 'Academia',
      description: 'Projeto de revitalização dos espaços internos da Academia Gustavo Borges, com foco na melhoria da funcionalidade, acolhimento e identidade visual da marca. O trabalho incluiu atualização da paleta de cores com tonalidades vibrantes e acolhedoras, requalificação da iluminação com luminárias pendentes para criar ambientação mais aconchegante, redesign do mobiliário priorizando conforto e circulação, criação de vitrines internas para exposição de troféus e prêmios, e renovação completa dos banheiros infantis com ambiente lúdico, seguro e funcional.',
      images: [
        '/images/portfolio/academia-gustavo-borges-1.webp',
        '/images/portfolio/academia-gustavo-borges-2.webp',
        '/images/portfolio/academia-gustavo-borges-3.webp'
      ]
    },
    {
      id: 2,
      title: 'Mercado Gregolin – Projeto de Identidade Visual e Ambientação Comercial',
      category: 'Mercado',
      description: 'Projeto desenvolvido para o Mercado Gregolin com foco na criação de uma identidade visual forte e na valorização da experiência de compra. A consultoria incluiu desenvolvimento da nova fachada com linguagem visual moderna e acolhedora, comunicação visual interna personalizada valorizando os setores de hortifrúti, padaria, laticínios e bebidas, setorização estratégica do espaço para facilitar a jornada do cliente, paleta de cores e iluminação pensadas para destacar produtos e criar acolhimento, e implantação de um espaço café integrado ao ambiente. Um exemplo de como o design aliado à estratégia comercial pode transformar um mercado de bairro em um espaço convidativo, funcional e alinhado com as expectativas do consumidor contemporâneo.',
      images: [
        '/images/portfolio/mercado-gregolin-1.webp',
        '/images/portfolio/mercado-gregolin-2.webp',
        '/images/portfolio/mercado-gregolin-3.webp'
      ]
    },
    {
      id: 3,
      title: 'Papelaria Ballpen – Renovação de Fachada Comercial',
      category: 'Papelaria',
      description: 'Para a tradicional Papelaria Ballpen, foi desenvolvido um projeto de fachada com o objetivo de modernizar a identidade visual do ponto comercial, reforçar o posicionamento da marca e melhorar sua visibilidade urbana. O projeto contemplou: Redesign completo da fachada, com linhas contemporâneas, materiais de destaque e aplicação da nova identidade visual de forma estratégica. Valorização do logotipo e elementos gráficos, trazendo unidade estética e fortalecendo a presença da marca na paisagem urbana. Uso de cores institucionais com alto contraste visual, facilitando a identificação do segmento da loja (papelaria e presentes) à distância. Letreiros iluminados e integração visual dos toldos, criando um visual limpo, moderno e convidativo.',
      images: [
        '/images/portfolio/papelaria-ballpen-1.webp'
      ]
    },
    {
      id: 4,
      title: 'Elemento Feminino – Visual Merchandising e Experiência de Compra',
      category: 'Confecção',
      description: 'Na loja de confecção Elemento Feminino, foi realizado um projeto completo de Visual Merchandising, com foco na valorização dos produtos e no desenho estratégico da jornada do cliente. O trabalho incluiu: Criação de vitrine conceito, com ambientação que traduz o estilo da marca e convida o cliente a entrar. Composição de looks e posicionamento dos manequins para estimular o desejo e facilitar a leitura de estilo. Organização dos espaços de entrada e circulação, criando um fluxo intuitivo e agradável desde o primeiro contato visual até o provador. Exposição dos produtos por cores, coleções e ocasiões, aumentando a percepção de valor e otimizando o tempo de permanência do cliente. Inserção de elementos naturais e pontos focais internos, como plantas e iluminação de destaque, para criar conexão emocional e reforçar a identidade da marca.',
      images: [
        '/images/portfolio/elemento-feminino-1.webp',
        '/images/portfolio/elemento-feminino-2.webp'
      ]
    }
  ];

  useEffect(() => {
    if (inView) {
      controls.start('visible');
    }
  }, [controls, inView]);

  const nextProject = () => {
    setCurrentProject((prev) => {
      const next = (prev + 1) % projects.length;
      // Scroll to top of portfolio section on mobile
      if (window.innerWidth < 768) {
        const portfolioSection = document.getElementById('portfolio');
        if (portfolioSection) {
          portfolioSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }
      return next;
    });
  };

  const prevProject = () => {
    setCurrentProject((prev) => {
      const previous = prev === 0 ? projects.length - 1 : prev - 1;
      // Scroll to top of portfolio section on mobile
      if (window.innerWidth < 768) {
        const portfolioSection = document.getElementById('portfolio');
        if (portfolioSection) {
          portfolioSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }
      return previous;
    });
  };

  const goToProject = (index: number) => {
    setCurrentProject(index);
    // Scroll to top of portfolio section on mobile
    if (window.innerWidth < 768) {
      const portfolioSection = document.getElementById('portfolio');
      if (portfolioSection) {
        portfolioSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  // Garantir que sempre temos um projeto válido
  const currentProjectData = projects[currentProject] || projects[0];

  return (
    <section id="portfolio" className="section bg-white py-20">
      <div className="container">
        <div className="text-center mb-16">
          <motion.h2 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-3xl md:text-4xl font-bold text-neutral-dark mb-4"
          >
            Portfólio
          </motion.h2>
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-lg text-neutral-dark/70 max-w-2xl mx-auto"
          >
            Conheça alguns dos projetos desenvolvidos por Raquel de Araújo, combinando estética, funcionalidade e inovação.
          </motion.p>
        </div>

        <motion.div
          ref={ref}
          variants={containerVariants}
          initial="hidden"
          animate={controls}
          className="max-w-6xl mx-auto"
        >
          {currentProjectData && (
            <div key={`project-${currentProject}`}>
              {/* Mobile Navigation - Top */}
              <div className="flex justify-center items-center gap-4 mb-8 md:hidden">
                <button 
                  onClick={prevProject}
                  className="w-10 h-10 rounded-full bg-neutral-light text-neutral-dark hover:bg-primary hover:text-white transition-colors flex items-center justify-center"
                  aria-label="Projeto anterior"
                >
                  ←
                </button>
                
                <div className="flex gap-2">
                  {projects.map((_, index) => (
                    <button 
                      key={`top-dot-${index}`}
                      className={`w-3 h-3 rounded-full transition-colors ${
                        currentProject === index ? 'bg-primary' : 'bg-neutral-light'
                      }`}
                      onClick={() => goToProject(index)}
                      aria-label={`Ir para projeto ${index + 1}`}
                    />
                  ))}
                </div>
                
                <button 
                  onClick={nextProject}
                  className="w-10 h-10 rounded-full bg-neutral-light text-neutral-dark hover:bg-primary hover:text-white transition-colors flex items-center justify-center"
                  aria-label="Próximo projeto"
                >
                  →
                </button>
              </div>

              {/* Project Info - Above Images */}
              <div className="flex flex-col md:flex-row justify-between items-start md:items-end mb-8">
                <div className="mb-4 md:mb-0">
                  <h3 className="text-2xl md:text-3xl font-bold text-neutral-dark mb-2">
                    {currentProjectData.title}
                  </h3>
                  <div className="flex flex-wrap gap-4 text-sm text-neutral-dark/70 mb-4">
                    <span className="bg-neutral-light px-3 py-1 rounded-full">
                      {currentProjectData.category}
                    </span>
                  </div>
                </div>
                
                <div className="hidden md:block">
                  <button 
                    className="text-primary hover:text-primary/80 transition-colors flex items-center gap-2"
                    onClick={() => {
                      const message = `Olá! Vi o projeto "${currentProjectData.title}" no seu portfólio e gostaria de saber mais sobre seus serviços.`;
                      const encodedMessage = encodeURIComponent(message);
                      window.open(`https://wa.me/5541999119143?text=${encodedMessage}`, '_blank');
                    }}
                  >
                    Solicitar Orçamento
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M5 12h14M12 5l7 7-7 7"/>
                    </svg>
                  </button>
                </div>
              </div>

              {/* Project Images Grid */}
              <div className={`gap-4 mb-8 ${
                currentProjectData.images?.length === 1 
                  ? 'flex justify-center' 
                  : currentProjectData.images?.length === 2 
                    ? 'grid grid-cols-1 md:grid-cols-2' 
                    : 'grid grid-cols-1 md:grid-cols-3'
              }`}>
                {currentProjectData.images?.map((image, index) => (
                  <div
                    key={`image-${currentProject}-${index}`}
                    className={`aspect-w-16 aspect-h-12 rounded-lg overflow-hidden shadow-md ${
                      currentProjectData.images?.length === 1 ? 'max-w-md' : ''
                    }`}
                  >
                    <img 
                      src={image} 
                      alt={`${currentProjectData.title} - ${index + 1}`}
                      className="w-full h-64 md:h-80 object-cover"
                    />
                  </div>
            ))}
              </div>

              {/* Project Description */}
              <p className="text-lg text-neutral-dark/80 max-w-4xl">
                {currentProjectData.description}
              </p>
            </div>
          )}

          {/* Desktop and Mobile Bottom Navigation */}
          <div className="flex justify-center items-center gap-4 mt-12">
            <button 
              onClick={prevProject}
              className="w-10 h-10 rounded-full bg-neutral-light text-neutral-dark hover:bg-primary hover:text-white transition-colors flex items-center justify-center"
              aria-label="Projeto anterior"
            >
              ←
            </button>
            
            <div className="flex gap-2">
              {projects.map((_, index) => (
                <button 
                  key={`bottom-dot-${index}`}
                  className={`w-3 h-3 rounded-full transition-colors ${
                    currentProject === index ? 'bg-primary' : 'bg-neutral-light'
                  }`}
                  onClick={() => goToProject(index)}
                  aria-label={`Ir para projeto ${index + 1}`}
                />
              ))}
            </div>
            
            <button 
              onClick={nextProject}
              className="w-10 h-10 rounded-full bg-neutral-light text-neutral-dark hover:bg-primary hover:text-white transition-colors flex items-center justify-center"
              aria-label="Próximo projeto"
            >
              →
            </button>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Portfolio; 